import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { EnhancedChatInputScreen } from "@/components/enhanced-chat-input-screen";
import { useUser } from "@/contexts/UserContext";
import { Loader2, MessageSquarePlus, Plus } from "lucide-react";
import { useDialog } from "../contexts/DialogContext";
import { getConversationName } from "../data/helpers";

export function Conversation() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { userData, loading } = useUser();
  const { openNewConversation } = useDialog();
  const conversations = userData?.conversations || [];

  useEffect(() => {
    if (loading || !userData) return;
    const conversations = userData.conversations || [];

    // Returning users continue to their latest conversation.
    if (!conversationId && conversations.length > 0) {
      const latestConversation = conversations[conversations.length - 1];
      navigate(`/c/${latestConversation.id}`);
      return;
    }

    // If conversationId is provided but not found in userData conversations, redirect to root
    if (
      conversationId &&
      !conversations.find((conv) => conv.id === conversationId)
    ) {
      navigate("/");
      return;
    }
  }, [conversationId, loading, navigate, userData]);

  if (!conversationId && (loading || conversations.length > 0)) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <Loader2 className="size-6 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!conversationId) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="text-center">
          <MessageSquarePlus
            className="mx-auto size-12 text-gray-400"
            aria-hidden="true"
          />
          <h3 className="mt-2 text-base font-semibold text-gray-900">
            Get started by creating a new conversation
          </h3>
          <div className="mt-6">
            <button
              type="button"
              onClick={openNewConversation}
              className="inline-flex items-center rounded-md bg-purple-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600"
            >
              <Plus className="-ml-0.5 mr-1.5 size-5" aria-hidden="true" />
              New Conversation
            </button>
          </div>
        </div>
      </div>
    );
  }

  const conversation = conversations.find((item) => item.id === conversationId);

  return (
    <EnhancedChatInputScreen
      conversationId={conversationId}
      targetName={conversation?.targetName || ""}
      conversationName={conversation ? getConversationName(conversation) : ""}
    />
  );
}
